// client/src/components/comics/ComicsDashboard.js
import React from 'react';
import CollectionPage from '../shared/CollectionPage';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { Navigate } from 'react-router-dom';

const ComicsDashboard = ({ wishlist = false }) => {
  const { featureFlags } = useFeatureFlags();

  if (!featureFlags.enableComicsFeature) {
    return <Navigate to="/" replace />;
  }

  const filterOptions = [
    { value: '', label: 'All Publishers' },
    { value: 'Marvel', label: 'Marvel' },
    { value: 'DC', label: 'DC' },
    { value: 'Image', label: 'Image' },
    { value: 'Dark Horse', label: 'Dark Horse' },
    { value: 'IDW', label: 'IDW' },
    { value: 'BOOM!', label: 'BOOM!' },
  ];

  const proxyImage = (url) => url ? `/api/proxy-image?url=${encodeURIComponent(url)}` : '';

  const renderProps = {
    getImageUrl: (item) => item.processedImageUrl || (item.imageUrl ? proxyImage(item.imageUrl) : ''),
    getItemName: (item) => `${item.series} #${item.issueNumber}`,
    getItemSubtitle: (item) => {
      const parts = [];
      if (item.publisher) parts.push(item.publisher);
      if (item.year) parts.push(String(item.year));
      if (item.storyArc) parts.push(item.storyArc);
      if (item.isGraded && item.gradingCompany && item.grade) {
        parts.push(`${item.gradingCompany} ${item.grade}`);
      }
      return parts.join(' · ');
    },
    getItemDetails: (item) => {
      const parts = [];
      if (item.writer) parts.push(`W: ${item.writer}`);
      if (item.artist) parts.push(`A: ${item.artist}`);
      if (item.condition) parts.push(item.condition);
      if (item.isFirstPrint) parts.push('1st Print');
      return parts.join(' · ');
    },
  };

  const getFilterValue = (item) => item.publisher;

  const editFields = [
    { name: 'series',      label: 'Series Title',   type: 'text', required: true, gridProps: { xs: 12 } },
    { name: 'issueNumber', label: 'Issue Number',    type: 'text', required: true, gridProps: { xs: 12, sm: 6 } },
    { name: 'publisher',   label: 'Publisher',       type: 'text', gridProps: { xs: 12, sm: 6 } },
    { name: 'year',        label: 'Cover Year',      type: 'number', gridProps: { xs: 12, sm: 6 } },
    { name: 'writer',      label: 'Writer',          type: 'text', gridProps: { xs: 12, sm: 6 } },
    { name: 'artist',      label: 'Artist / Penciler', type: 'text', gridProps: { xs: 12, sm: 6 } },
    { name: 'storyArc',    label: 'Story Arc',       type: 'text', gridProps: { xs: 12, sm: 6 } },
    { name: 'coverVariant', label: 'Cover Variant',  type: 'text', gridProps: { xs: 12, sm: 6 } },
    { name: 'isFirstPrint', label: '1st Print',      type: 'checkbox', gridProps: { xs: 12, sm: 6 } },
    {
      name: 'condition', label: 'Condition', type: 'select',
      options: [
        { value: 'Poor',       label: 'Poor' },
        { value: 'Fair',       label: 'Fair' },
        { value: 'Good',       label: 'Good' },
        { value: 'Very Good',  label: 'Very Good' },
        { value: 'Fine',       label: 'Fine' },
        { value: 'Very Fine',  label: 'Very Fine' },
        { value: 'Near Mint',  label: 'Near Mint' },
        { value: 'Near Mint+', label: 'Near Mint+' },
      ],
      gridProps: { xs: 12, sm: 6 },
    },
    { name: 'isGraded',       label: 'Graded Slab',      type: 'checkbox', gridProps: { xs: 12, sm: 6 } },
    { name: 'gradingCompany', label: 'Grading Company',   type: 'text',     showWhen: (d) => !!d.isGraded, gridProps: { xs: 12, sm: 6 } },
    { name: 'grade',          label: 'Grade (e.g. 9.8)', type: 'number',   showWhen: (d) => !!d.isGraded, gridProps: { xs: 12, sm: 6 } },
    { name: 'imageUrl',  label: 'Image URL',  type: 'text',     gridProps: { xs: 12 } },
    { name: 'price',     label: 'Price Paid', type: 'text',     gridProps: { xs: 12, sm: 6 } },
    { name: 'rating',    label: 'Rating',     type: 'rating',   gridProps: { xs: 12 } },
    { name: 'notes',     label: 'Notes',      type: 'textarea', gridProps: { xs: 12 } },
  ];

  const tableColumns = {
    series:      { label: 'Series',    sortable: true },
    issueNumber: { label: 'Issue',     sortable: true },
    publisher:   { label: 'Publisher', sortable: true },
    year:        { label: 'Year',      sortable: true, numeric: true },
    condition:   { label: 'Condition', sortable: true },
    grade:       { label: 'Grade',     sortable: true, numeric: true },
  };

  return (
    <CollectionPage
      collectionType={wishlist ? 'comics-wishlist' : 'comics'}
      title={wishlist ? 'Comics Wishlist' : 'Comics Collection'}
      renderProps={renderProps}
      filterOptions={filterOptions}
      getFilterValue={getFilterValue}
      editFields={editFields}
      tableColumns={tableColumns}
    />
  );
};

export default ComicsDashboard;
